import { analyseTheme } from "../../../analyseTheme";

export const INCIDENTS_CHART_GROUPS_FLAG = "_drawLabelGroups";
export const INCIDENTS_CHART_ROWS_PER_CLASS = "_rowsPerClass";

export type LabelGroupBand = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export type LabelGroupSeparator = {
  top: number;
  left: number;
  width: number;
};

const GROUP_BAND_PADDING = 6;

const getRowView = (chart: any, rowIndex: number) => {
  const datasets: any[] = chart.data?.datasets || [];
  for (let datasetIndex = 0; datasetIndex < datasets.length; datasetIndex += 1) {
    const meta = chart.getDatasetMeta(datasetIndex);
    if (meta.hidden) {
      continue;
    }
    const bar = meta.data[rowIndex];
    if (bar && !bar.hidden && bar._view && bar._view.height > 0) {
      return bar._view;
    }
  }
  return null;
};

export const collectLabelGroupOverlays = (
  chart: any,
  rowsPerClass: number
): { bands: LabelGroupBand[]; separators: LabelGroupSeparator[] } => {
  const rowCount: number = chart?.data?.labels?.length || 0;
  const chartArea = chart?.chartArea;
  if (!chartArea || rowCount === 0 || rowsPerClass <= 0) {
    return { bands: [], separators: [] };
  }

  const groupCount = Math.ceil(rowCount / rowsPerClass);
  const bands: LabelGroupBand[] = [];
  const separators: LabelGroupSeparator[] = [];
  const plotLeft = chartArea.left;
  const plotWidth = chartArea.right - chartArea.left;

  for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
    const startIdx = groupIndex * rowsPerClass;
    const endIdx = Math.min(startIdx + rowsPerClass - 1, rowCount - 1);

    const firstVm = getRowView(chart, startIdx);
    const lastVm = getRowView(chart, endIdx);
    if (!firstVm || !lastVm) {
      continue;
    }

    const top =
      Math.min(
        firstVm.y - firstVm.height / 2,
        lastVm.y - lastVm.height / 2
      ) - GROUP_BAND_PADDING;
    const bottom =
      Math.max(
        firstVm.y + firstVm.height / 2,
        lastVm.y + lastVm.height / 2
      ) + GROUP_BAND_PADDING;

    bands.push({
      top,
      left: plotLeft,
      width: plotWidth,
      height: bottom - top,
    });

    if (groupIndex > 0) {
      const prevEndIdx = startIdx - 1;
      const prevVm = getRowView(chart, prevEndIdx);
      if (prevVm && firstVm) {
        const separatorTop =
          (prevVm.y + prevVm.height / 2 + (firstVm.y - firstVm.height / 2)) /
          2;
        separators.push({
          top: separatorTop,
          left: plotLeft,
          width: plotWidth,
        });
      }
    }
  }

  return { bands, separators };
};

export const drawLabelGroupBands = (
  ctx: CanvasRenderingContext2D,
  bands: LabelGroupBand[]
) => {
  bands.forEach((band, index) => {
    ctx.fillStyle = index % 2 === 0 ? analyseTheme.accentSoft : "#f6f7fa";
    ctx.fillRect(band.left, band.top, band.width, band.height);
  });
};

export const drawLabelGroupSeparators = (
  ctx: CanvasRenderingContext2D,
  separators: LabelGroupSeparator[]
) => {
  separators.forEach((separator) => {
    ctx.save();
    ctx.strokeStyle = analyseTheme.border;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(separator.left, separator.top);
    ctx.lineTo(separator.left + separator.width, separator.top);
    ctx.stroke();
    ctx.restore();
  });
};
